import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { Users, FileBadge2, Wallet, UserCircle2, ArrowRight } from 'lucide-react';
import { RootState } from '../../redux/store';
import { adminApi, paymentsApi } from '../../services/api';
import { PaymentRequest } from '../../types/customer';
import DashboardLayout from '../../components/layout/DashboardLayout';
import AdminSidebar from '../../components/sidebar/AdminSidebar';
import EnterpriseSpendChart from '../../components/dashboard/EnterpriseSpendChart';
import './AdminDashboard.css';

interface DashboardData {
  pendingRegistrations: number;
  pendingDocuments: number;
  pendingPayments: number;
  totalUsers: number;
  recentActivity?: Array<{ type: string; message: string; time: string }>;
}

interface TrendPoint {
  label: string;
  spend: number;
  outflow: number;
}

const outflowStatuses = new Set(['approved', 'processing', 'completed']);

const buildSixMonthTrend = (payments: PaymentRequest[]): TrendPoint[] => {
  const now = new Date();
  const months = Array.from({ length: 6 }, (_, index) => {
    const date = new Date(now.getFullYear(), now.getMonth() - (5 - index), 1);
    return {
      key: `${date.getFullYear()}-${date.getMonth()}`,
      label: date.toLocaleString('en-US', { month: 'short' }),
      spend: 0,
      outflow: 0,
    };
  });

  payments.forEach((payment) => {
    if (!payment.createdAt) return;
    const createdAt = new Date(payment.createdAt);
    if (Number.isNaN(createdAt.getTime())) return;

    const monthKey = `${createdAt.getFullYear()}-${createdAt.getMonth()}`;
    const bucket = months.find((month) => month.key === monthKey);
    if (!bucket) return;

    const amount = Number(payment.localAmount || 0);
    bucket.spend += amount;
    if (outflowStatuses.has(payment.status)) {
      bucket.outflow += amount;
    }
  });

  return months;
};

const AdminDashboard: React.FC = () => {
  const user = useSelector((state: RootState) => state.auth.user);
  const [dashboardData, setDashboardData] = useState<DashboardData | null>(null);
  const [paymentTrend, setPaymentTrend] = useState<TrendPoint[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true);
        const [dashboardResponse, allPaymentsResponse] = await Promise.all([
          adminApi.getDashboard(),
          paymentsApi.getAllPayments(),
        ]);

        setDashboardData(dashboardResponse);
        setPaymentTrend(buildSixMonthTrend(allPaymentsResponse.payments || []));
        setError(null);
      } catch (requestError) {
        console.error('Failed to fetch dashboard data:', requestError);
        setError('Failed to load dashboard data');
        setDashboardData({
          pendingRegistrations: 0,
          pendingDocuments: 0,
          pendingPayments: 0,
          totalUsers: 0,
          recentActivity: [],
        });
        setPaymentTrend(
          buildSixMonthTrend([])
        );
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  const stats = dashboardData || {
    pendingRegistrations: 0,
    pendingDocuments: 0,
    pendingPayments: 0,
    totalUsers: 0,
    recentActivity: [],
  };

  const metricCards = useMemo(
    () => [
      {
        title: 'Pending Registrations',
        value: stats.pendingRegistrations,
        subtitle: 'Accounts awaiting onboarding',
        icon: <Users size={20} />,
        link: '/admin/pending-registrations',
      },
      {
        title: 'Document Approvals',
        value: stats.pendingDocuments,
        subtitle: 'KYC files awaiting review',
        icon: <FileBadge2 size={20} />,
        link: '/admin/document-approvals',
      },
      {
        title: 'Payment Queue',
        value: stats.pendingPayments,
        subtitle: 'Requests waiting for decision',
        icon: <Wallet size={20} />,
        link: '/admin/payment-requests',
      },
      {
        title: 'Total Users',
        value: stats.totalUsers,
        subtitle: 'Active users on platform',
        icon: <UserCircle2 size={20} />,
        link: '/admin/user-management',
      },
    ],
    [stats.pendingRegistrations, stats.pendingDocuments, stats.pendingPayments, stats.totalUsers]
  );

  return (
    <DashboardLayout sidebar={<AdminSidebar />}>
      <div className="admin-dashboard">
        <div className="dashboard-header">
          <h2>Welcome, {user?.name || 'Admin'}.</h2>
          <p className="welcome-subtitle">
            Enterprise operations cockpit for approvals, payments, and risk controls.
          </p>
        </div>

        {loading && <div className="loading">Loading dashboard data...</div>}
        {error && <div className="error">{error}</div>}

        {!loading && (
          <>
            <div className="stats-grid">
              {metricCards.map((card) => (
                <Link key={card.title} to={card.link} className="stat-card">
                  <div className="stat-icon">{card.icon}</div>
                  <div className="stat-content">
                    <h3>{card.value}</h3>
                    <p>{card.title}</p>
                    <small>{card.subtitle}</small>
                  </div>
                  <ArrowRight size={16} className="stat-arrow" />
                </Link>
              ))}
            </div>

            <EnterpriseSpendChart
              title="Spend Over Time vs Wallet Outflows"
              subtitle="6-month trend based on submitted requests and approved/processed payouts."
              points={paymentTrend}
              currencyCode="NGN"
            />

            <div className="quick-actions">
              <h3>Critical Workflows</h3>
              <div className="actions-grid">
                <div className="action-card">
                  <h4>Registration Pipeline</h4>
                  <p>Assess KYC and move users from pending to approved states quickly.</p>
                  <Link to="/admin/pending-registrations" className="action-link">
                    Review registrations
                  </Link>
                </div>

                <div className="action-card">
                  <h4>Document Decisions</h4>
                  <p>Use evidence-driven approvals and rejection notes for compliance traceability.</p>
                  <Link to="/admin/document-approvals" className="action-link">
                    Open document approvals
                  </Link>
                </div>

                <div className="action-card">
                  <h4>Payment Control</h4>
                  <p>Review high-value transfers and synchronize status with Reap lifecycle.</p>
                  <Link to="/admin/payment-requests" className="action-link">
                    Open payment queue
                  </Link>
                </div>

                <div className="action-card">
                  <h4>Exchange Management</h4>
                  <p>Set authoritative rates and maintain transparent audit notes for treasury.</p>
                  <Link to="/admin/bank-account" className="action-link">
                    Manage rates
                  </Link>
                </div>
              </div>
            </div>

            <div className="recent-activity">
              <h3>Latest Operations</h3>
              <div className="activity-list">
                {stats.recentActivity && stats.recentActivity.length > 0 ? (
                  stats.recentActivity.map((activity, index) => (
                    <div key={`${activity.time}-${index}`} className="activity-item">
                      <div className="activity-content">
                        <p>{activity.message}</p>
                        <span className="activity-time">
                          {new Date(activity.time).toLocaleString()}
                        </span>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="activity-item">
                    <div className="activity-content">
                      <p>No recent activity</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </DashboardLayout>
  );
};

export default AdminDashboard;
